import Link from 'next/link';

import { LinkedinIcon } from '../../icons/linkedin';
import { YoutubeIcon } from '../../icons/youtube';
import { cn } from '../../lib/utils';

type SocialPlatform = 'linkedin' | 'youtube';

interface FooterSection {
  heading: React.ReactNode;
  links: Array<{
    href: string;
    label: React.ReactNode;
    newTab?: boolean;
  }>;
}

interface SocialLink {
  platform: SocialPlatform;
  href: string;
}

interface FooterProps extends React.HTMLAttributes<HTMLElement> {
  logo: React.ReactNode;
  description: React.ReactNode;
  copyright: React.ReactNode;
  sections: FooterSection[];
  socialLinks: SocialLink[];
}

export function Footer({
  className,
  logo,
  description,
  copyright,
  sections,
  socialLinks,
  ...props
}: FooterProps) {
  return (
    <footer
      className={cn(
        'site-footer relative mt-auto w-full py-8 2xl:py-20',
        className,
      )}
      {...props}
    >
      <div className="container">
        <div className="flex flex-col gap-8 lg:flex-row lg:gap-0">
          <div className="flex w-full gap-x-3 lg:w-4/12 xl:w-4/12 xl:gap-6 2xl:gap-8">
            <div className="flex flex-col gap-y-4">
              <div>{logo}</div>

              <div className="flex flex-col gap-y-4">
                <div>
                  <p className="text-muted-foreground text-sm tracking-tight">
                    {description}
                  </p>
                </div>
                <div className="flex gap-2">
                  {socialLinks.map((link) => (
                    <SocialLink key={link.href} {...link} />
                  ))}
                </div>
                <div className="text-muted-foreground flex text-xs">
                  <p>{copyright}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex w-full flex-col gap-8 lg:flex-row lg:justify-end lg:gap-0 lg:gap-6 xl:gap-16">
            {sections.map((section, index) => (
              <div key={index}>
                <div className="flex flex-col gap-y-2.5">
                  <FooterSectionHeading>{section.heading}</FooterSectionHeading>

                  <FooterSectionList>
                    {section.links.map((link, linkIndex) => (
                      <FooterLink
                        key={linkIndex}
                        href={link.href}
                        newTab={!!link.newTab}
                      >
                        {link.label}
                      </FooterLink>
                    ))}
                  </FooterSectionList>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}

function FooterSectionHeading(props: React.PropsWithChildren) {
  return (
    <span className="font-heading text-sm font-semibold tracking-tight">
      {props.children}
    </span>
  );
}

function FooterSectionList(props: React.PropsWithChildren) {
  return <ul className="flex flex-col gap-y-2">{props.children}</ul>;
}

function FooterLink({
  href,
  newTab,
  children,
}: React.PropsWithChildren<{ href: string; newTab: boolean }>) {
  return (
    <li className="text-muted-foreground text-sm tracking-tight hover:underline [&>a]:transition-colors">
      <a
        href={href}
        target={newTab ? '_blank' : undefined}
        // See https://github.com/jsx-eslint/eslint-plugin-react/blob/master/docs/rules/jsx-no-target-blank.md
        rel={newTab ? 'noopener noreferrer' : undefined}
      >
        {children}
      </a>
    </li>
  );
}

function SocialLink(props: SocialLink) {
  return (
    <Link
      href={props.href}
      className="opacity-80 transition-opacity hover:opacity-100"
      target="_blank"
      rel="noopener noreferrer"
    >
      {props.platform === 'linkedin' && <LinkedinIcon className="size-8" />}
      {props.platform === 'youtube' && <YoutubeIcon className="size-8" />}
    </Link>
  );
}
